import { create } from 'zustand';
import { STATUSES_VISIBLE_TO_OTHERS } from '../utils/presenceStatus';

/**
 * OTHER people's presence, one status per user, as told by the server.
 *
 * `{ byUserId: { '123': 'online' | 'away' | 'busy' | 'offline' } }` — keys are
 * STRINGS (Phoenix Presence keys metas by a stringified user id, and the
 * `presence:sync` payload is a JSON object, so its keys are strings whatever
 * the id's type is on the wire). Every reader must `String(userId)` before
 * looking one up; `usePresenceStatus` is the only reader there is.
 *
 * ── Absent key vs `'offline'` ──────────────────────────────────────────────
 * A missing key means UNKNOWN, and unknown renders nothing. It is not the same
 * as `'offline'` and the two are never merged:
 *
 *   - before the first `presence:sync` we have not asked yet;
 *   - on the free (local-only) plan there is no socket at all, so this store
 *     stays permanently empty and every foreign avatar keeps rendering nothing,
 *     exactly as it did before this feature;
 *   - the server deliberately OMITS ids the viewer is not entitled to see
 *     rather than reporting them offline (Stage 1 contract, addition 2) — an
 *     `"offline"` for a stranger would be an answer about a stranger and would
 *     make the watch set an account-existence oracle.
 *
 * ── Re-render contract ─────────────────────────────────────────────────────
 * `byUserId` is REPLACED on every write (zustand state is immutable), so its
 * identity churns on every ping. That is fine and is the whole point of the
 * one-selector rule in `usePresenceStatus`: a selector returning
 * `byUserId[id] ?? null` returns a string or null, and `Object.is` suppresses
 * the render for every user whose own status did not change. No component may
 * ever select `byUserId` itself — see `presenceRerenderContract.test.js`.
 *
 * `applySync` writes the whole snapshot in ONE `set()`. N individual `set()`
 * calls would be N render passes: React 18 batches inside event handlers but
 * NOT inside a socket callback, which is where every one of these lands.
 */

/** Statuses the wire may carry about somebody else. Anything else is dropped. */
function sanitize(status) {
  return STATUSES_VISIBLE_TO_OTHERS.includes(status) ? status : null;
}

export const useUserPresenceStore = create((set) => ({
  byUserId: {},

  /**
   * Whole-snapshot write, from `presence:sync`. REPLACES the map rather than
   * merging: the snapshot is the server's complete answer for the watch set we
   * just sent, so a user we stopped watching must go back to unknown (no dot)
   * instead of keeping a status that is no longer being maintained.
   */
  applySync: (statuses) => {
    const byUserId = {};
    for (const [userId, status] of Object.entries(statuses || {})) {
      const clean = sanitize(status);
      if (clean) byUserId[String(userId)] = clean;
    }
    set({ byUserId });
  },

  /** Single-user write, from `presence:update`. */
  applyUpdate: (userId, status) => {
    if (userId == null) return;
    const clean = sanitize(status);
    if (!clean) return;
    set((s) =>
      s.byUserId[String(userId)] === clean
        ? s // no-op: do not churn the map (or any selector) for an unchanged status
        : { byUserId: { ...s.byUserId, [String(userId)]: clean } }
    );
  },

  /** Back to "we know nothing" — logout, socket teardown, account switch. */
  clearPresence: () => set({ byUserId: {} }),
}));
